import type { ReactNode } from "react";
import { mailboxLabel, type DeliveryBoard, type MailboxInfo } from "../../api";
import { formatShortTimestamp } from "../../format";
import {
  dayLabel,
  hhmm,
  isProblem,
  KINDS,
  plural,
  tallies,
  tallyText,
  type Case,
  type Tone,
} from "./model";

// The top of the Deliveries board: the board's own health (is it still true?)
// and the verdict, the one line to read from across the room.

export const watcherStale = (board: DeliveryBoard, now: number): boolean =>
  board.watcher.deployed && (board.watcher.lastRun === null || now - Date.parse(board.watcher.lastRun) > board.watcher.staleAfterMinutes * 60_000);

/** The API's caveats, plus a watcher that stopped since the last refresh. */
export function caveatsOf(board: DeliveryBoard, now: number): string[] {
  const unsure = [...board.caveats];
  if (watcherStale(board, now) && !unsure.some((caveat) => caveat.includes("watcher"))) unsure.push("the delivery watcher has stopped, so nothing alerts");
  return unsure;
}

interface Notice {
  tone: Tone;
  node: ReactNode;
}

/**
 * Whether the board can be believed: the poller that fetches receipts, the
 * watcher that alerts, files it could not read. Each is a loud banner when
 * wrong and a quiet clause when fine.
 */
function healthOf(board: DeliveryBoard, info: MailboxInfo, now: number): Notice[] {
  const notices: Notice[] = [];
  const name = mailboxLabel(info);
  const { receipts, watcher } = board;
  if (!receipts.deployed) notices.push({ tone: "quiet", node: <>No poller for {name} here: receipts are not fetched, so none can be called missing.</> });
  else if (!receipts.scheduled)
    notices.push({ tone: "warn", node: <><b>{name}'s receipts are not fetched by this service.</b> Deliveries stay waiting here; read their receipts where they are fetched.</> });
  else if (!receipts.lastRun)
    notices.push({ tone: "bad", node: <><b>No completed poll of {name} in the last three hours.</b> Receipts may not be arriving: see the poller's log under Admin.</> });
  else
    notices.push({
      tone: receipts.lastRun.failed > 0 ? "bad" : "quiet",
      node: (
        <>
          Receipts polled hourly, last at {hhmm(receipts.lastRun.at)} ({plural(receipts.lastRun.fetched, "file")}
          {receipts.lastRun.failed > 0 ? <b>, {receipts.lastRun.failed} could not be fetched</b> : null}). Missing after {board.missingAfterMinutes} min.
        </>
      ),
    });
  if (!watcher.deployed) notices.push({ tone: "warn", node: <><b>No delivery watcher here:</b> nothing on this board alerts anyone.</> });
  else if (watcherStale(board, now))
    notices.push({
      tone: "bad",
      node: (
        <>
          <b>The delivery watcher has not run {watcher.lastRun ? `since ${formatShortTimestamp(watcher.lastRun)}` : "in the last two hours"}: no alerts are being sent.</b>{" "}
          Check its schedule and its log under Admin.
        </>
      ),
    });
  else notices.push({ tone: "quiet", node: <>Alerts: the watcher last ran at {watcher.lastRun ? hhmm(watcher.lastRun) : "—"} (every 15 min).</> });
  if (board.unreadable.length > 0)
    notices.push({
      tone: "bad",
      node: (
        <>
          <b>{plural(board.unreadable.length, "file")} could not be read and {board.unreadable.length === 1 ? "is" : "are"} not judged:</b>{" "}
          <span className="mono">{board.unreadable.join(", ")}</span>
        </>
      ),
    });
  if (board.truncated) notices.push({ tone: "bad", node: <><b>The listing is incomplete:</b> a folder holds more files than one listing reads; the oldest are not included.</> });
  return notices;
}

export interface Freshness {
  loading: boolean;
  updatedAt: Date | undefined;
  failure: { at: Date; error: string } | undefined;
  stale: boolean;
}

const clock = new Intl.DateTimeFormat("da-DK", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/** The board's own truth: a failed refresh, a stopped watcher, an unpolled mailbox. Above everything. */
export function Health({ board, info, now, freshness }: { board: DeliveryBoard; info: MailboxInfo; now: number; freshness: Freshness }): JSX.Element {
  const notices = healthOf(board, info, now);
  const loud = notices.filter((n) => n.tone !== "quiet");
  const quiet = notices.filter((n) => n.tone === "quiet");
  const { failure, stale, updatedAt } = freshness;
  return (
    <>
      {failure || stale ? (
        <p className="board-alarm" data-tone="bad" role="alert">
          <span className="board-alarm-mark">!</span>
          <span>
            <b>
              {failure ? `Could not refresh at ${clock.format(failure.at)}: ${failure.error.replace(/\.$/, "")}.` : `Not refreshed since ${updatedAt ? clock.format(updatedAt) : "—"}.`}
            </b>{" "}
            {updatedAt ? `What you see is as of ${clock.format(updatedAt)}` : "Nothing could be shown yet"} — retrying every minute.
          </span>
        </p>
      ) : null}
      {loud.map((n, i) => (
        <p key={i} className="board-alarm" data-tone={n.tone}>
          <span className="board-alarm-mark">{n.tone === "bad" ? "!" : "?"}</span>
          <span>{n.node}</span>
        </p>
      ))}
      {quiet.length > 0 ? (
        <p className="board-health">
          {quiet.map((n, i) => (
            <span key={i}>{n.node}</span>
          ))}
        </p>
      ) : null}
    </>
  );
}

/** The one line to read from across the room. */
export function Verdict({ board, cases, now }: { board: DeliveryBoard; cases: Case[]; now: number }): JSX.Element {
  const problems = cases.filter(isProblem);
  const unsure = caveatsOf(board, now);
  const counted = tallies(cases);
  const waiting = counted.find((t) => t.kind === "waiting");
  const accepted = counted.find((t) => t.kind === "accepted");
  const tone: Tone = problems.some((c) => KINDS[c.kind].tone === "bad") || unsure.length > 0 ? "bad" : problems.length > 0 ? "warn" : "good";
  const total = board.deliveries.length;
  let mark: string;
  let headline: string;
  if (problems.length > 0) {
    mark = tone === "bad" ? "✗" : "?";
    headline = `${problems.length} ${problems.length === 1 ? "thing needs" : "things need"} attention`;
  } else if (unsure.length > 0) {
    mark = "?";
    headline = "No problem found — but this cannot be vouched for";
  } else if (total === 0) {
    mark = "✓";
    headline = "No deliveries in this window";
  } else {
    mark = "✓";
    headline = waiting ? `All good — ${waiting.deliveries} waiting for receipts` : `All good — ${total} of ${total} accepted`;
  }
  return (
    <section className="board-verdict" data-tone={tone}>
      <span className="board-verdict-mark" aria-hidden="true">
        {mark}
      </span>
      <div>
        <h3>{headline}</h3>
        <p>
          {problems.length > 0 ? (
            <>
              {counted
                .filter((t) => KINDS[t.kind].tone === "bad" || KINDS[t.kind].tone === "warn")
                .map((t) => (
                  <span key={t.kind} className="board-tally" data-tone={KINDS[t.kind].tone}>
                    {KINDS[t.kind].mark} {tallyText(t)}
                  </span>
                ))}
              <span className="board-tally" data-tone="quiet">
                {[accepted ? tallyText(accepted) : null, waiting ? tallyText(waiting) : null].filter(Boolean).join(" · ")}
              </span>
            </>
          ) : unsure.length > 0 ? (
            <span className="board-tally" data-tone="bad">
              {unsure.join("; ")}
            </span>
          ) : total > 0 ? (
            <span className="board-tally" data-tone="quiet">
              {plural(total, "delivery", "deliveries")} uploaded since {dayLabel(board.since)}, each with a positive T and V from Nets
              {waiting ? `; ${waiting.deliveries} just uploaded` : ""}.
            </span>
          ) : null}
        </p>
      </div>
    </section>
  );
}

/** A state: a mark and a word, coloured only when it is a problem. */
export function Mark({ tone, mark, word }: { tone: Tone; mark: string; word: string }): JSX.Element {
  return (
    <span className="board-mark" data-tone={tone}>
      <span aria-hidden="true">{mark}</span> {word}
    </span>
  );
}

